import dayjs from 'dayjs'
import React, { useRef, useState } from 'react'
import useDisclosure from '../../hooks/useDisclosure'
import { formatTimeDigit } from '../../utils/global'
import { range } from '../../utils/locDash'
import Box from '../Box'
import Button from '../Button'
import Dropdown from '../Dropdown'
import Flex from '../Flex'
import Icon from '../Icon'
import Input from '../Input'
import Text from '../Text'
import { TimePickerProps } from './DatePicker.types'
import { Divider, Time, TimeWrapper } from './styled'

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  customInput,
  isMinuteZero,
  disabled,
}) => {
  const disclosure = useDisclosure({ open: false })

  const [hour, setHour] = useState<number>(value ? dayjs(value).hour() : dayjs().hour())
  const [minute, setMinute] = useState<number>(value ? dayjs(value).minute() : dayjs().minute())
  const [selectedHour, setSelectedHour] = useState<number>(hour)
  const [selectedMinute, setSelectedMinute] = useState<number>(minute)

  const onConfirm = () => {
    disclosure.onClose()
    onChange &&
      onChange(dayjs(`${selectedHour}:${selectedMinute}`), [selectedHour, selectedMinute].join(':'))
    setHour(selectedHour)
    setMinute(selectedMinute)
  }

  return disabled ? (
    <Box width='150px'>
      <Input
        label=''
        placeholder='Input placeholder'
        value='-'
        sufix={<Icon name='Time-solid' size={20} />}
        disabled={disabled}
      />
    </Box>
  ) : (
    <Dropdown control={disclosure}>
      <Dropdown.Toggle>
        {customInput?.({
          plain: { hour, minute },
          formattedTime: { hour: formatTimeDigit(hour), minute: formatTimeDigit(minute) },
        }) || (
          <Input
            label=''
            placeholder='Input placeholder'
            value={`${formatTimeDigit(hour)}:${formatTimeDigit(minute)}`}
            prefix={<Icon name='Calendar-outline' size={20} />}
          />
        )}
      </Dropdown.Toggle>
      <Dropdown.Content aria-label='dropdown-time-picker' overrideWidth={true}>
        <Flex flexDirection='column' alignItems='center'>
          <Flex
            alignItems='center'
            overflow='hidden'
            position='relative'
            padding={16}
            columnGap='8px'
          >
            <TimeWrapper
              overflowY='auto'
              overflowX='hidden'
              flexDirection='column'
              height={170}
              rowGap='2px'
            >
              <Flex height='0px' flexShrink={0} />
              {range(24).map((_, i) => {
                const ref = useRef<HTMLDivElement>(null)
                const isActive = i === selectedHour
                return (
                  <Time
                    key={`hour-${i}`}
                    ref={ref}
                    aria-label={`hour-${i}`}
                    onClick={() => {
                      setSelectedHour(i)
                      ref.current.scrollIntoView({
                        behavior: 'auto',
                        block: 'center',
                        inline: 'center',
                      })
                    }}
                    paddingY='4px'
                    borderRadius='8px'
                    backgroundColor={isActive ? 'blue50' : 'white'}
                    cursor='pointer'
                    alignItems='center'
                    justifyContent='center'
                    width='48px'
                    isActive={isActive}
                  >
                    <Text
                      color={isActive ? 'white' : 'black75'}
                      weight={isActive ? 'bold' : 'regular'}
                    >
                      {formatTimeDigit(i)}
                    </Text>
                  </Time>
                )
              })}
            </TimeWrapper>
            <Text variant='subtitle' weight='bold' color='black50'>
              :
            </Text>
            <TimeWrapper
              overflowY='auto'
              overflowX='hidden'
              flexDirection='column'
              height={170}
              rowGap='2px'
            >
              <Flex height='0px' flexShrink={0} />
              {range(60).map((_, i) => {
                const ref = useRef<HTMLDivElement>(null)
                const isActive = i === selectedMinute

                return (
                  <Time
                    key={`minute-${i}`}
                    ref={ref}
                    aria-label={`minute-${i}`}
                    onClick={() => {
                      if (isMinuteZero) {
                        setSelectedMinute(0)
                      } else {
                        setSelectedMinute(i)
                      }
                      ref.current.scrollIntoView({
                        behavior: 'auto',
                        block: 'center',
                        inline: 'center',
                      })
                    }}
                    paddingY='4px'
                    borderRadius='8px'
                    backgroundColor={isActive ? 'blue50' : 'white'}
                    cursor='pointer'
                    alignItems='center'
                    justifyContent='center'
                    width='48px'
                    isActive={isActive}
                  >
                    <Text
                      color={isActive ? 'white' : 'black75'}
                      weight={isActive ? 'bold' : 'regular'}
                    >
                      {isMinuteZero ? '00' : formatTimeDigit(i)}
                    </Text>
                  </Time>
                )
              })}
            </TimeWrapper>
          </Flex>
          <Divider />
          <Box width='120px' padding={16}>
            <Button type='button' aria-label='save-time-picker' isBlock onClick={onConfirm}>
              Simpan
            </Button>
          </Box>
        </Flex>
      </Dropdown.Content>
    </Dropdown>
  )
}

export default TimePicker
